import { useState } from 'react';
import ChordDiagram from '@/components/ChordDiagram';
import PianoChord from '@/components/PianoChord';
import BassChord from '@/components/BassChord';
import DrumPattern from '@/components/DrumPattern';
import { getChordShape } from '@/lib/chords';
import { Guitar, Piano, Music2, Drum } from 'lucide-react';

// Regex to detect chord tokens. Matches letters A-G, optional #/b, optional m/maj/dim/sus/aug and digits/b5.
const CHORD_RE = /\b([A-G](?:#|b)?(?:maj|min|m|dim|aug|sus)?(?:\d{1,2})?(?:sus\d)?(?:add\d)?(?:b\d)?)\b/g;

/**
 * ChordChart renders the chord chart text with chord tokens highlighted as clickable pills.
 * Below the chart, it renders per-instrument visualisations (Guitar / Piano / Bass diagrams,
 * or a rhythmic Drum pattern) with a toggle.
 */
export default function ChordChart({ text, timeSignature = '4/4', bpm }) {
  const [view, setView] = useState('guitar');
  if (!text) return null;

  const found = new Set();
  const lines = text.split('\n');

  const rendered = lines.map((line, li) => {
    const parts = [];
    let lastIdx = 0;
    for (const m of line.matchAll(CHORD_RE)) {
      const chord = m[1];
      if (!getChordShape(chord)) continue;
      const idx = m.index;
      if (idx > lastIdx) parts.push({ type: 'text', text: line.slice(lastIdx, idx) });
      parts.push({ type: 'chord', text: chord });
      found.add(chord);
      lastIdx = idx + chord.length;
    }
    if (lastIdx < line.length) parts.push({ type: 'text', text: line.slice(lastIdx) });
    if (parts.length === 0) return <div key={li} className="min-h-[1.5em]">&nbsp;</div>;
    return (
      <div key={li} className="font-mono text-sm leading-7 whitespace-pre-wrap">
        {parts.map((p, i) => p.type === 'chord' ? (
          <span key={i} className="inline-block px-1.5 py-0.5 rounded-md bg-orange-950/60 text-orange-300 border border-orange-800/50 font-semibold mx-0.5">
            {p.text}
          </span>
        ) : (
          <span key={i}>{p.text}</span>
        ))}
      </div>
    );
  });

  const uniqueChords = Array.from(found);

  const TABS = [
    { key: 'guitar', label: 'Guitare', Icon: Guitar },
    { key: 'piano',  label: 'Piano',   Icon: Piano },
    { key: 'bass',   label: 'Basse',   Icon: Music2 },
    { key: 'drums',  label: 'Batterie', Icon: Drum },
  ];

  return (
    <div>
      <div className="card-stone p-6 overflow-x-auto">{rendered}</div>
      {uniqueChords.length > 0 && (
        <div className="mt-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <div className="overline text-stone-400">
              {view === 'drums'
                ? `Pattern batterie · ${timeSignature}`
                : `Diagrammes d'accords (${uniqueChords.length})`}
            </div>
            <div className="inline-flex bg-stone-900 border border-stone-800 rounded-full p-1 flex-wrap" role="tablist">
              {TABS.map(({ key, label, Icon }) => (
                <button
                  key={key}
                  data-testid={`diagram-view-${key}`}
                  onClick={() => setView(key)}
                  className={`px-3 py-1 rounded-full text-xs flex items-center gap-1.5 transition-colors ${view === key ? 'bg-orange-700 text-white' : 'text-stone-400 hover:text-stone-200'}`}
                  aria-selected={view === key}
                >
                  <Icon size={13} /> {label}
                </button>
              ))}
            </div>
          </div>
          {view === 'drums' ? (
            <DrumPattern timeSignature={timeSignature} bpm={bpm} />
          ) : (
            <div className="flex flex-wrap gap-4">
              {uniqueChords.map((c) => {
                if (view === 'guitar') return <ChordDiagram key={c} name={c} size={92} />;
                if (view === 'piano')  return <PianoChord    key={c} name={c} size={80} />;
                return <BassChord key={c} name={c} size={80} />;
              })}
            </div>
          )}
          {view === 'bass' && (
            <p className="mt-3 text-xs text-stone-500 italic">
              <span className="inline-block w-2 h-2 rounded-full bg-orange-700 mr-1.5"></span>
              R = fondamentale (root) · <span className="inline-block w-2 h-2 rounded-full bg-stone-500 mx-1.5"></span>5 = quinte. Manche vu du haut (E-A-D-G), 5 premières cases.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
